/**
 * Arrière-plans de la caméra : catalogue et image perso.
 *
 * Réglage `settings.videoBackground` : 'none', 'blur-light', 'blur',
 * 'photo:{id}' (décor du catalogue ci-dessous) ou 'custom' (image choisie par
 * la personne). L'image perso reste dans ce navigateur (localStorage), réduite
 * en JPEG : elle n'est jamais envoyée au serveur, seule l'image composée par
 * la caméra part dans la vidéo.
 *
 * Décors : photos libres (licences Unsplash/Pexels : usage commercial, sans
 * crédit obligatoire), servies par Placio sous /assets/backgrounds/, générées
 * par `pnpm --filter @campus/assets-cli ingest-backgrounds` (1920 × 1080 et
 * vignette 320 × 180 en WebP, `?v=` dans `background_catalog.json`).
 */
import catalog from './background_catalog.json';

export type VideoBackground = 'none' | 'blur-light' | 'blur' | 'custom' | `photo:${string}`;

export interface BackgroundPhoto {
  id: string;
  label: string;
}

/** Décors proposés, dans l'ordre d'affichage. */
export const BACKGROUND_PHOTOS: readonly BackgroundPhoto[] = (catalog as { photos: BackgroundPhoto[] }).photos;

const VERSIONS = (catalog as { versions: Record<string, string> }).versions;

export function backgroundPhotoUrl(id: string, thumb = false): string {
  const file = thumb ? `${id}-thumb` : id;
  return `/assets/backgrounds/${file}.webp?v=${VERSIONS[file] ?? '1'}`;
}

export function isVideoBackground(value: unknown): value is VideoBackground {
  if (value === 'none' || value === 'blur-light' || value === 'blur' || value === 'custom') return true;
  return typeof value === 'string' && value.startsWith('photo:') && BACKGROUND_PHOTOS.some((p) => `photo:${p.id}` === value);
}

// ── Image perso ───────────────────────────────────────────────

const CUSTOM_KEY = 'placio:videoBackgroundImage';
const CUSTOM_WIDTH = 1280;
const CUSTOM_HEIGHT = 720;

// Image perso remplacée ou effacée : le réglage reste 'custom', les caméras
// doivent quand même reprendre la nouvelle image.
const customListeners = new Set<() => void>();

export function onCustomBackgroundChange(fn: () => void): () => void {
  customListeners.add(fn);
  return () => { customListeners.delete(fn); };
}

const notifyCustomChange = () => { for (const fn of customListeners) fn(); };

export function loadCustomBackground(): string | null {
  try { return localStorage.getItem(CUSTOM_KEY); } catch { return null; }
}

export function clearCustomBackground(): void {
  try { localStorage.removeItem(CUSTOM_KEY); } catch { /* rien à effacer */ }
  notifyCustomChange();
}

/**
 * Image choisie par la personne → JPEG 1280 × 720 recadré au centre, gardé
 * dans ce navigateur. Renvoie l'adresse `data:` ou lève une erreur lisible.
 */
export async function saveCustomBackground(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Choisis une image (JPEG, PNG, WebP).');
  const bitmap = await createImageBitmap(file).catch(() => { throw new Error('Cette image n’a pas pu être lue.'); });
  const canvas = document.createElement('canvas');
  canvas.width = CUSTOM_WIDTH;
  canvas.height = CUSTOM_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Cette image n’a pas pu être lue.');
  const scale = Math.max(CUSTOM_WIDTH / bitmap.width, CUSTOM_HEIGHT / bitmap.height);
  const w = bitmap.width * scale;
  const h = bitmap.height * scale;
  ctx.drawImage(bitmap, (CUSTOM_WIDTH - w) / 2, (CUSTOM_HEIGHT - h) / 2, w, h);
  bitmap.close();
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  try {
    localStorage.setItem(CUSTOM_KEY, dataUrl);
  } catch {
    throw new Error('Ton navigateur n’a pas pu garder cette image.');
  }
  notifyCustomChange();
  return dataUrl;
}
